import { Box, Stack, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import NGLogo from '@/features/themedLogos/NGLogo'
import useAuth from '@/hooks/useAuth'

const options = [
    {
        label: 'Vertical',
        value: 'vertical',
        icon: (
            <Box
                sx={{
                    display: 'flex',
                    flex: '1 1 auto'
                }}
            >
                <Box
                    sx={(theme) => ({
                        borderRightColor: 'neutral.700',
                        borderRightStyle: 'dashed',
                        borderRightWidth: 1,
                        px: 1,
                        py: 0.5,
                        ...theme.applyStyles('dark', {
                            borderRightColor: 'neutral.500'
                        })
                    })}
                >
                    <Stack spacing={1}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                height: 20,
                                width: 10
                            }}
                        >
                            <NGLogo />
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: 'primary.main',
                                borderRadius: '2px',
                                height: 4,
                                width: 26
                            }}
                        />
                        <Box
                            sx={(theme) => ({
                                backgroundColor: 'neutral.300',
                                borderRadius: '2px',
                                height: 4,
                                width: 26,
                                ...theme.applyStyles('dark', {
                                    backgroundColor: 'neutral.600'
                                })
                            })}
                        />
                        <Box
                            sx={(theme) => ({
                                backgroundColor: 'neutral.300',
                                borderRadius: '2px',
                                height: 4,
                                width: 26,
                                ...theme.applyStyles('dark', {
                                    backgroundColor: 'neutral.600'
                                })
                            })}
                        />
                    </Stack>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flex: '1 1 auto',
                        p: 1
                    }}
                >
                    <Box
                        sx={(theme) => ({
                            backgroundColor: 'background.paper',
                            borderColor: 'neutral.700',
                            borderRadius: 1,
                            borderStyle: 'dashed',
                            borderWidth: 1,
                            flex: '1 1 auto',
                            ...theme.applyStyles('dark', {
                                borderColor: 'neutral.500'
                            })
                        })}
                    />
                </Box>
            </Box>
        )
    },
    {
        label: 'Horizontal',
        value: 'horizontal',
        icon: (
            <Box
                sx={{
                    display: 'flex',
                    flex: '1 1 auto',
                    flexDirection: 'column'
                }}
            >
                <Box
                    sx={(theme) => ({
                        borderBottomColor: 'neutral.700',
                        borderBottomStyle: 'dashed',
                        borderBottomWidth: 1,
                        px: 1,
                        ...theme.applyStyles('dark', {
                            borderBottomColor: 'neutral.500'
                        })
                    })}
                >
                    <Stack alignItems="center" direction="row" spacing={1}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                height: 24,
                                width: 20
                            }}
                        >
                            <NGLogo />
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: 'primary.main',
                                borderRadius: '2px',
                                height: 4,
                                width: 16
                            }}
                        />
                        <Box
                            sx={(theme) => ({
                                backgroundColor: 'neutral.300',
                                borderRadius: '2px',
                                height: 4,
                                width: 16,
                                ...theme.applyStyles('dark', {
                                    backgroundColor: 'neutral.600'
                                })
                            })}
                        />
                        <Box
                            sx={(theme) => ({
                                backgroundColor: 'neutral.300',
                                borderRadius: '2px',
                                height: 4,
                                width: 16,
                                ...theme.applyStyles('dark', {
                                    backgroundColor: 'neutral.600'
                                })
                            })}
                        />
                    </Stack>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flex: '1 1 auto',
                        p: 1
                    }}
                >
                    <Box
                        sx={(theme) => ({
                            backgroundColor: 'background.paper',
                            borderColor: 'neutral.700',
                            borderRadius: 1,
                            borderStyle: 'dashed',
                            borderWidth: 1,
                            flex: '1 1 auto',
                            ...theme.applyStyles('dark', {
                                borderColor: 'neutral.500'
                            })
                        })}
                    />
                </Box>
            </Box>
        )
    }
]

export const OptionsLayout = (props) => {
    const { onChange, value } = props
    const { settings } = useAuth()

    return (
        <Stack spacing={1}>
            <Typography
                color="text.secondary"
                variant="overline"
                sx={{ mt: 4 }}
            >
                Layout
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: 'repeat(2, minmax(0, 140px))'
                }}
            >
                {options.map((option) => (
                    <Stack key={option.value} spacing={1}>
                        <Box
                            onClick={() => onChange?.(option.value)}
                            sx={[
                                {
                                    backgroundColor: 'background.paper',
                                    borderColor: 'divider',
                                    borderRadius: settings.style.roundedCorners
                                        ? 1
                                        : 0,
                                    borderStyle: 'solid',
                                    borderWidth: 2,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    height: 88
                                },
                                option.value === value && {
                                    borderColor: 'primary.main'
                                }
                            ]}
                        >
                            {option.icon}
                        </Box>
                        <Typography
                            align="center"
                            sx={{ fontWight: 500 }}
                            variant="body2"
                        >
                            {option.label}
                        </Typography>
                    </Stack>
                ))}
            </Box>
        </Stack>
    )
}

OptionsLayout.propTypes = {
    onChange: PropTypes.func,
    value: PropTypes.oneOf(['horizontal', 'vertical'])
}
